import type { CSSProperties } from "react"
import { ArrowUpRight, Check, Gift, MailOpen, Plus, type LucideIcon } from "lucide-react"
import { Card } from "@workspace/ui/components/card"
import { Badge } from "@workspace/ui/components/badge"
import { Separator } from "@workspace/ui/components/separator"
import { Button } from "@workspace/ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@workspace/ui/components/dialog"
import { cn } from "@workspace/ui/lib/utils"
import { useI18n } from "@/lib/i18n"
import { ImageSlot } from "./image-slot"

export type ProductCardItem = {
  badge: string
  label: string
  title: string
  copy: string
  meta: string
  href: string | null
  linkLabel: string | null
  overview: string
  features: readonly string[]
  audience: string
  stack: readonly string[]
  pricing: string
}

type ProductCardProps = ProductCardItem & {
  variant?: "default" | "muted"
  number: string
  index?: number
}

export function ProductCard({
  badge,
  label,
  title,
  copy,
  meta,
  href,
  linkLabel,
  overview,
  features,
  audience,
  stack,
  pricing,
  variant = "default",
  number,
  index = 0,
}: ProductCardProps) {
  const { t } = useI18n()
  const isDisabled = !href
  const imageTone: "brand" | "neutral" | "dark" =
    variant === "muted" ? "neutral" : index === 0 ? "brand" : "dark"
  const sections = t.products.sections
  const ICONS_BY_LABEL: Record<string, LucideIcon> = {
    Rewards: Gift,
    Events: MailOpen,
  }
  const SlotIcon = ICONS_BY_LABEL[label]

  return (
    <Dialog>
      <Card
        style={{ "--d": index + 2 } as CSSProperties}
        className={cn(
          "group relative gap-0 overflow-hidden p-0 transition-all duration-300",
          variant === "default"
            ? "border-border bg-card hover:border-primary/50 hover:-translate-y-0.5"
            : "border-dashed border-foreground/25 bg-transparent",
        )}
      >
        {/* Entire upper half opens the dialog */}
        <DialogTrigger asChild>
          <button
            type="button"
            className="relative w-full cursor-pointer text-left"
            aria-label={`${t.products.openLabel} — ${label}`}
          >
            <ImageSlot
              ratio="5 / 3"
              tone={imageTone}
              caption={`Blumen · ${label}`}
              icon={SlotIcon}
              className="border-0 border-b border-border"
            />
            {/* Hover reveal — the plus indicator */}
            <span
              aria-hidden
              className="absolute right-4 top-4 inline-flex size-9 items-center justify-center border border-border bg-background/85 text-foreground backdrop-blur-sm transition-all duration-300 group-hover:border-primary/60 group-hover:text-primary"
            >
              <Plus className="size-4" strokeWidth={1.6} />
            </span>
          </button>
        </DialogTrigger>

        <div className="flex flex-1 flex-col gap-5 p-5 md:p-6">
          <div className="flex items-start justify-between gap-3">
            <span className="font-mono text-[11px] tracking-[0.04em] text-primary">
              {number}.
            </span>
            <Badge variant={isDisabled ? "muted" : "brand"}>{badge}</Badge>
          </div>

          <div>
            <div className="eyebrow-sm inline-flex items-center gap-1.5 text-foreground/70">
              {SlotIcon ? (
                <SlotIcon className="size-3 text-primary" strokeWidth={1.8} aria-hidden />
              ) : null}
              Blumen<span className="text-foreground/25"> / </span>
              <span className="text-foreground">{label}</span>
            </div>

            <h3 className="mt-3 text-[1.15rem] leading-[1.2] font-bold tracking-[-0.02em] text-foreground md:text-[1.25rem]">
              {title}
            </h3>

            <p className="mt-2 text-[13.5px] leading-[1.6] text-foreground/65">
              {copy}
            </p>
          </div>

          <div className="mt-auto">
            <Separator className="mb-3" />
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/70">
                {meta}
              </span>
              <DialogTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 rounded-none px-2 font-mono text-[10px] uppercase tracking-[0.18em] text-primary hover:bg-primary/10 hover:text-primary"
                >
                  {t.products.openLabel}
                  <ArrowUpRight size={12} />
                </Button>
              </DialogTrigger>
            </div>
          </div>
        </div>
      </Card>

      {/* Dialog: detail view */}
      <DialogContent>
        {/* Slim brand accent bar — carries the product tone without re-using the hero image */}
        <div
          aria-hidden
          className={cn(
            "h-2",
            imageTone === "brand"
              ? "bg-primary"
              : imageTone === "dark"
                ? "bg-foreground"
                : "bg-foreground/20",
          )}
        />

        <div className="flex flex-col gap-8 p-8">
          <header className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Badge variant={isDisabled ? "muted" : "brand"}>{badge}</Badge>
              <span className="eyebrow-sm inline-flex items-center gap-1.5 text-foreground/70">
                {SlotIcon ? (
                  <SlotIcon className="size-3 text-primary" strokeWidth={1.8} aria-hidden />
                ) : null}
                Blumen<span className="text-foreground/25"> / </span>
                <span className="text-foreground">{label}</span>
              </span>
            </div>
            <DialogTitle>{title}</DialogTitle>
          </header>

          {/* Overview */}
          <section>
            <h4 className="font-mono text-[10px] uppercase tracking-[0.24em] text-primary">
              {sections.overview}
            </h4>
            <DialogDescription className="mt-3">{overview}</DialogDescription>
          </section>

          {/* Features */}
          <section>
            <h4 className="font-mono text-[10px] uppercase tracking-[0.24em] text-primary">
              {sections.features}
            </h4>
            <ul className="mt-3 grid grid-cols-1 gap-x-8 gap-y-3 md:grid-cols-2">
              {features.map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-2.5 text-[14.5px] leading-[1.55] text-foreground"
                >
                  <Check
                    className="mt-[4px] size-4 shrink-0 text-primary"
                    strokeWidth={2}
                    aria-hidden
                  />
                  {f}
                </li>
              ))}
            </ul>
          </section>

          {/* Audience + Pricing side-by-side */}
          <section className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <h4 className="font-mono text-[10px] uppercase tracking-[0.24em] text-primary">
                {sections.audience}
              </h4>
              <p className="mt-3 text-[14.5px] leading-[1.6] text-foreground/80">
                {audience}
              </p>
            </div>
            <div>
              <h4 className="font-mono text-[10px] uppercase tracking-[0.24em] text-primary">
                {sections.pricing}
              </h4>
              <p className="mt-3 text-[14.5px] leading-[1.6] text-foreground/80">
                {pricing}
              </p>
            </div>
          </section>

          {/* Stack */}
          <section>
            <h4 className="font-mono text-[10px] uppercase tracking-[0.24em] text-primary">
              {sections.stack}
            </h4>
            <div className="mt-3 flex flex-wrap gap-2">
              {stack.map((s) => (
                <Badge key={s} variant="outline">
                  {s}
                </Badge>
              ))}
            </div>
          </section>

          {/* CTA */}
          {href && linkLabel && (
            <div className="flex items-center justify-end gap-2 border-t border-border pt-6">
              <Button
                asChild
                variant="outline"
                size="md"
                className="rounded-none border-primary bg-transparent font-mono text-[11px] uppercase tracking-[0.18em] text-primary hover:bg-primary/10 hover:text-primary"
              >
                <a
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                >
                  {linkLabel}
                  <ArrowUpRight size={14} />
                </a>
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
